import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { servicesData } from '../data/services';

const Header = ({ onGetStarted }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Our Attorneys', path: '/attorneys' },
    { name: 'Services', path: '#', hasDropdown: true },
    { name: 'Contact Us', path: '/contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'backdrop-blur-xl bg-white/90 border-b border-[#2C3E50]/10 shadow-sm' : 'bg-white/95 border-b border-[#2C3E50]/5'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="/assets/img/brand/logo-dark.png" 
              alt="Federal IP Office" 
              className="h-[4rem] w-auto"
              data-testid="header-logo"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <div key={link.name} className="relative">
                {link.hasDropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setIsServicesOpen(true)}
                    onMouseLeave={() => setIsServicesOpen(false)}
                  >
                    <button
                      className="flex items-center space-x-1 text-[#2E2E2E] hover:text-[#2C3E50] transition-colors font-medium"
                      data-testid="services-dropdown-trigger"
                    >
                      <span>{link.name}</span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    {isServicesOpen && (
                      <div
                        className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[320px] bg-white shadow-xl border border-[#2C3E50]/5 py-4"
                        data-testid="services-dropdown-menu"
                      >
                        <div className="flex flex-col">
                          {servicesData.map((service) => (
                            <Link
                              key={service.id}
                              to={service.path}
                              className="px-6 py-3 hover:bg-[#F8F9FA] transition-colors text-[#2E2E2E] hover:text-[#2C3E50]"
                              data-testid={`service-link-${service.id}`}
                            >
                              {service.title}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    className={`text-[#2E2E2E] hover:text-[#2C3E50] transition-colors font-medium ${
                      location.pathname === link.path ? 'text-[#2C3E50]' : ''
                    }`}
                    data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button */}
          <button
            onClick={onGetStarted}
            className="hidden lg:block bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-6 py-3 font-medium"
            data-testid="header-cta-button"
          >
            Get Legal Help
          </button>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-[#2C3E50]"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-[#2C3E50]/10 py-4 max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
            <nav className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.hasDropdown ? (
                    <div>
                      <button
                        onClick={() => setIsServicesOpen(!isServicesOpen)}
                        className="flex items-center justify-between w-full text-[#2E2E2E] font-medium py-2"
                        data-testid="mobile-services-toggle"
                      >
                        <span>{link.name}</span>
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${
                            isServicesOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {isServicesOpen && (
                        <div className="pl-4 mt-2 space-y-2">
                          {servicesData.map((service) => (
                            <Link
                              key={service.id}
                              to={service.path}
                              onClick={() => {
                                setIsMobileMenuOpen(false);
                                setIsServicesOpen(false);
                              }}
                              className="block text-sm text-[#2E2E2E]/80 hover:text-[#2C3E50] py-2"
                              data-testid={`mobile-service-link-${service.id}`}
                            >
                              {service.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block text-[#2E2E2E] hover:text-[#2C3E50] font-medium py-2"
                      data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
              <div className="pt-4">
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    onGetStarted();
                  }}
                  className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-6 py-3 font-medium w-full"
                  data-testid="mobile-cta-button"
                >
                  Get Legal Help
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
